import { Box } from "@mui/material";
import React, { Dispatch, useEffect, useState } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import CustomButton from "../components/common/CustomButton";
import CustomLabel from "../components/common/CustomLabel";
import CustomTextField from "../components/common/CustomTextField";
import {
  Container,
  GlobalReset,
  LeftPanel,
  OuterContainer,
  RightPanel,
} from "../components/layout/CommonLoginLayout";
import { TwoFactorMethod, userOtpSelect, userOtpVerify } from "../services/AuthService";
import { COLORS } from "../assets/theme";
import ToastNotification from "../components/ToastNotification";

type PendingOtpSession = {
  uuid: string;
  availableMethods: TwoFactorMethod[];
  method?: TwoFactorMethod;
  otpExpiresInSeconds?: number;
};

const PENDING_OTP_SESSION_KEY = "pendingOtpSession";

function readPendingOtpSession(): PendingOtpSession | null {
  const raw = sessionStorage.getItem(PENDING_OTP_SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingOtpSession>;
    if (!parsed.uuid) return null;
    const methods = Array.isArray(parsed.availableMethods)
      ? (parsed.availableMethods.filter(Boolean) as TwoFactorMethod[])
      : [];
    return {
      uuid: parsed.uuid,
      availableMethods: methods,
      method: parsed.method,
      otpExpiresInSeconds: parsed.otpExpiresInSeconds,
    };
  } catch {
    return null;
  }
}

function writePendingOtpSession(next: PendingOtpSession) {
  sessionStorage.setItem(PENDING_OTP_SESSION_KEY, JSON.stringify(next));
}

const LoginOTP = () => {
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem("access_token")
  );

  useEffect(() => {
    if (accessToken) localStorage.setItem("access_token", accessToken);
  }, [accessToken]);

  if (accessToken) {
    return <Navigate to="/" />;
  }
  return <UnAuthed setAccessToken={setAccessToken} />;
};

interface IUnAuthedProps {
  setAccessToken: Dispatch<React.SetStateAction<string | null>>;
}

const UnAuthed = ({ setAccessToken }: IUnAuthedProps) => {
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState<boolean>(false);
  const [requestingOTP, setRequestingOTP] = useState<boolean>(false);
  const session = readPendingOtpSession();
  const pendingUuid = session?.uuid;
  const pendingTwoFactorMethod = session?.method;

  const [formData, setFormData] = useState({
    otp: "",
  });

  const [error, setError] = useState<{
    otp?: string;
    general?: string;
  }>({});

  const validateField = (name: string, value: string) => {
    let errorMessage = "";

    if (name === "otp") {
      if (!value) {
        errorMessage = "Code is required.";
      } else if (!/^\d{6}$/.test(value)) {
        errorMessage = "Code must be a 6-digit number.";
      }
    }

    return errorMessage;
  };

  const handleRequestOTP = async () => {
    if (requestingOTP) return;
    if (!pendingUuid) {
      ToastNotification.error("Missing code session. Please login again.");
      return;
    }
    if (!pendingTwoFactorMethod) {
      ToastNotification.error("Please choose a two-factor method first.");
      return;
    }

    setRequestingOTP(true);
    setError({});

    try {
      const response = await userOtpSelect(pendingUuid, pendingTwoFactorMethod);
      const otpMsg =
        response?.message ||
        "Code has been sent. Please proceed to code verification.";
      const otpExpires = response?.otpExpiresInSeconds;
      let infoMsg = otpMsg;
      if (otpExpires) infoMsg += `  ${otpExpires}s.`;
      ToastNotification.success(infoMsg, { timeOut: 10000 });

      writePendingOtpSession({
        uuid: pendingUuid,
        availableMethods: session?.availableMethods ?? [],
        method: pendingTwoFactorMethod,
        otpExpiresInSeconds: otpExpires,
      });

      setError({});
    } catch (error: any) {
      const errorMessage =
        error.message || "Failed to send code. Please try again.";
      setError({
        general: errorMessage,
      });
      ToastNotification.error(errorMessage);
    } finally {
      setRequestingOTP(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    const errorMessage = validateField(name, value);
    setError((prevError) => ({ ...prevError, [name]: errorMessage }));
  };

  const handleVerify = async () => {
    if (verifying) {
      return;
    }
    if (!pendingUuid) {
      ToastNotification.error("Missing code session. Please login again.");
      return;
    }
    if (!pendingTwoFactorMethod) {
      ToastNotification.error("Please choose a two-factor method first.");
      return;
    }
    setVerifying(true);
    const { otp } = formData;
    const newError = {
      otp: validateField("otp", otp),
    };

    if (newError.otp) {
      setError(newError);
      setVerifying(false);
      return;
    }

    try {
      const loginResponse = await userOtpVerify(
        pendingUuid,
        otp,
        pendingTwoFactorMethod
      );

      if (loginResponse && loginResponse.access_token) {
        if (loginResponse.refresh_token) {
          localStorage.setItem("refresh_token", loginResponse.refresh_token);
        }
        setAccessToken(loginResponse.access_token);
        sessionStorage.removeItem(PENDING_OTP_SESSION_KEY);
        window.location.reload();
      } else {
        const msg =
          loginResponse?.message || "Invalid response from server. Please try again.";
        ToastNotification.error(msg);
        throw new Error(msg);
      }
    } catch (error: any) {
      console.error("Failed to verify code:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to verify code. Please try again.";
      setError({ general: errorMessage });
      ToastNotification.error(errorMessage);
    } finally {
      setVerifying(false);
    }
  };

  if (!pendingUuid) {
    return <Navigate to="/login" replace />;
  }
  if (!pendingTwoFactorMethod) {
    return <Navigate to="/choose-2fa" replace />;
  }

  return (
    <>
      <Outlet />
      <GlobalReset />
      <OuterContainer>
        <Container>
          <RightPanel>
            <CustomLabel
              text="Code Verification"
              fontWeight="bold"
              fontSize="24px"
              color="#A39455"
              sx={{ marginBottom: 3, textAlign: "left", width: "100%" }}
            />

            <Box sx={{ width: { xs: "100%", md: "100%" }, mt: 2 }}>
              <CustomTextField
                label="Code"
                name="otp"
                type="text"
                value={formData.otp}
                onChange={handleChange}
                bgColor="#fff"
                fullWidth
                error={!!error.otp}
                helperText={error.otp}
                onKeyDown={({ key }) => {
                  if (key === "Enter") handleVerify();
                }}
              />
            </Box>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                justifyContent: "space-between",
                marginTop: 4,
              }}
            >
              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <CustomButton
                  label={requestingOTP ? "Sending..." : "Resend code"}
                  onClick={handleRequestOTP}
                  bgColor="#7A7A7A"
                  textColor="#fff"
                  borderRadius="4px"
                  size="medium"
                  sx={{ height: "40px", width: "100%" }}
                  disabled={requestingOTP}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", md: "48%" } }}>
                <CustomButton
                  label={verifying ? "Verifying..." : "Verify code"}
                  onClick={handleVerify}
                  bgColor="#A39455"
                  textColor="#fff"
                  borderRadius="4px"
                  size="medium"
                  sx={{ height: "40px", width: "100%" }}
                  disabled={verifying}
                />
              </Box>
            </Box>

            <Box sx={{ width: "100%", mt: 2 }}>
              <CustomButton
                label="Change method"
                onClick={() => navigate("/choose-2fa")}
                bgColor="#f5f5f5"
                textColor="#6b6b6b"
                borderRadius="4px"
                size="medium"
                sx={{ height: "40px", width: "100%", border: "1px solid #A39455" }}
                disabled={verifying || requestingOTP}
              />
            </Box>
          </RightPanel>
          <LeftPanel
            sx={{
              maxHeight: { xs: "80vh" },
              pt: { xs: 6, sm: 7, md: 12 },
            }}
          >
            <Box
              component="img"
              src="/SPARK LOGO-white.png"
              alt="Logo"
              sx={{
                width: { xs: "200px", sm: "250px", md: "300px" },
                height: "auto",
                marginBottom: { xs: "10px", md: "20px" },
              }}
            />

            <CustomLabel
              text="Secure Two-Factor Authentication"
              sx={{
                fontSize: { xs: "14px", sm: "16px", md: "17px" },
                textAlign: "center",
              }}
              color={COLORS.GOLD}
              marginTop="40px"
              fontWeight="bold"
            />

            <CustomLabel
              text="Enter the code sent to your email address to securely verify your identity and access your account with confidence."
              sx={{
                fontSize: { xs: "12px", sm: "14px", md: "15px" },
                fontWeight: 400,
                lineHeight: "1.5",
                textAlign: "center",
                maxWidth: { xs: "80%", md: "600px" },
                color: "white",
                margin: "0 auto",
              }}
            />

            <CustomLabel
              text="Powered by Teways"
              color="#fff"
              fontWeight="bold"
              sx={{
                fontSize: { xs: "6px", sm: "7px", md: "8px" },
              }}
            />
          </LeftPanel>
        </Container>
      </OuterContainer>
    </>
  );
};

export default LoginOTP;
